import { AvatarGroup, AvatarGroupCount } from "@/components/ui/avatar";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { ProfileAvatar } from "@/features/avatar";
import { getInitials } from "@/features/avatar/avatar.utils";
import { getPresenceStatus, usePresenceState } from "@/features/avatar/use-presence";
import { isMemberType, resolveMemberDisplayName } from "@/features/containers/containers.utils";
import { AVATARS_STORAGE_BUCKET } from "@/features/profiles";
import { getAvatarOverflowLayout } from "@/features/projects/components/container-members-avatar-group.utils";
import { useManageMembersData } from "@/features/projects/components/container-members-dialog/api/use-manage-members-data";
import { ManageMembersDialog } from "@/features/projects/components/container-members-dialog/container-members-dialog";
import { KEY_ENTER, KEY_SPACE } from "@/lib/keyboard-keys";
import { GlobeIcon, LockIcon } from "lucide-react";
import { useMemo, useState } from "react";

const MAX_VISIBLE = 5;

type ContainerMembersAvatarGroupProps = {
  /** The chat, message room or document shown in the header. */
  containerId: string;
  /** Its parent project. */
  projectId: string;
  size?: number;
};

/**
 * Header avatars + lock/globe for a container, opening the shared
 * ManageMembersDialog (CR-177). Members and sharing come from
 * useManageMembersData — the same source the sidebar row menu uses.
 */
export function ContainerMembersAvatarGroup({
  containerId,
  projectId,
  size = 36,
}: ContainerMembersAvatarGroupProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const { container, containerLabel, roster } = useManageMembersData(containerId, projectId);
  const { humanMembers, aiMembers, availableHumanMembers, availableAiMembers } = roster;
  // `is_ai_shared` is read-only here: a document's AI access, or a legacy room
  // value (CR-177 removed the switch). When set, project personas fold in.
  const isShared = container?.is_shared ?? false;
  const isAiShared = container?.is_ai_shared ?? false;

  const onlineUsers = usePresenceState();

  const members = useMemo(() => {
    // Users and personas fold in the full project roster independently: users
    // when shared, personas when AI-shared. Otherwise only explicit members show.
    const effectiveHumans = isShared ? [...humanMembers, ...availableHumanMembers] : humanMembers;
    const effectiveAis = isAiShared ? [...aiMembers, ...availableAiMembers] : aiMembers;

    const human = effectiveHumans.map((member) => {
      if (isMemberType("user")(member)) {
        const name = resolveMemberDisplayName(member) ?? "";

        return {
          id: member.container_member_id,
          name,
          initials: getInitials({ fullName: name }),
          avatarPath: member.profile_avatar_url || null,
          activityStatus: getPresenceStatus(member, onlineUsers),
        };
      }

      const name = resolveMemberDisplayName(member) || "Invited user";
      return {
        id: member.container_member_id,
        name,
        initials: getInitials({ fullName: name }),
        avatarPath: null,
        activityStatus: getPresenceStatus(member, onlineUsers),
      };
    });

    const ai = effectiveAis.map((member) => {
      return {
        id: member.container_member_id,
        name: member.ai_persona_display_name,
        initials: getInitials({ fullName: member.ai_persona_display_name }),
        avatarPath: member.ai_persona_avatar_url || null,
        activityStatus: getPresenceStatus(member, onlineUsers),
      };
    });

    return [...human, ...ai];
  }, [
    isShared,
    isAiShared,
    humanMembers,
    availableHumanMembers,
    aiMembers,
    availableAiMembers,
    onlineUsers,
  ]);

  const { visibleCount, overflowCount } = getAvatarOverflowLayout(members.length, MAX_VISIBLE);
  const showPill = overflowCount > 0;
  const visibleMembers = members.slice(0, visibleCount);
  const hiddenMembers = showPill ? members.slice(visibleCount) : [];

  // Braced on purpose: wuchale's injected runtime hook must stay above it.
  if (!container || !containerLabel) {
    return null;
  }

  return (
    <>
      <div
        role="button"
        tabIndex={0}
        className="focus-visible:border-ring focus-visible:ring-ring/50 hover:border-border flex cursor-pointer items-center gap-1.5 rounded-md border border-transparent px-2 py-1 transition-all outline-none hover:opacity-75 hover:shadow-sm focus-visible:ring-[3px]"
        onClick={() => setDialogOpen(true)}
        onKeyDown={(e) => {
          if (e.key === KEY_ENTER || e.key === KEY_SPACE) setDialogOpen(true);
        }}
        aria-label={`Open ${containerLabel} members`}
      >
        <AvatarGroup>
          {visibleMembers.map((member) => (
            <ProfileAvatar
              key={member.id}
              avatarPath={member.avatarPath}
              fallbackInitials={member.initials}
              size={size}
              storageName={AVATARS_STORAGE_BUCKET}
              statusDot={member.activityStatus}
            />
          ))}

          {showPill ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <AvatarGroupCount style={{ width: size, height: size }}>
                  +{hiddenMembers.length}
                </AvatarGroupCount>
              </TooltipTrigger>
              <TooltipContent>
                <div className="flex flex-col gap-0.5">
                  {hiddenMembers.map((member) => (
                    <span key={member.id}>{member.name}</span>
                  ))}
                </div>
              </TooltipContent>
            </Tooltip>
          ) : null}
        </AvatarGroup>

        <Tooltip>
          <TooltipTrigger asChild>
            <span className="text-muted-foreground flex shrink-0 items-center">
              {isShared ? <GlobeIcon className="size-3.5" /> : <LockIcon className="size-3.5" />}
            </span>
          </TooltipTrigger>
          <TooltipContent>
            {isShared
              ? `All project members can access this ${containerLabel}`
              : `Only added members can access this ${containerLabel}`}
          </TooltipContent>
        </Tooltip>
      </div>

      <ManageMembersDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        containerId={containerId}
        projectId={projectId}
      />
    </>
  );
}
